import { Listbox, Transition } from "@headlessui/react"
import React, { Fragment, useRef, useLayoutEffect, useState } from "react"
import {
  ChatBubbleLeftIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  MegaphoneIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/solid"
import Icon from "../../icon"
import classNames from "../../utils/classNames"
import { CheckIcon } from "@heroicons/react/20/solid"
import PortalHoverTip from "./PortalHoverTip"

export interface Option {
  label: string | JSX.Element
  value: any
  hidden?: boolean
  disabled?: boolean
  tooltipText?: string
  isBottomPlacement?: boolean
  subOptions?: Option[]
  route?: string
}

interface ComponentProps {
  name?: string
  className?: string
  width?: string
  label?: string
  labelClassName?: string
  options: Array<Option>
  selected: Option | Option[]
  setSelected: (option: Option | Option[]) => void
  disabled?: boolean
  large?: boolean
  buttonSize?: "small" | "normal" | "large" | string
  isBottomPlacement?: boolean
  warningText?: JSX.Element
  multiple?: boolean
  buttonStyle?: React.CSSProperties
  dataTestId?: string
  noIcon?: boolean
  tooltip?: boolean
}

const SelectInput = ({
  name,
  className,
  width,
  label,
  labelClassName,
  options,
  selected,
  setSelected,
  disabled,
  large,
  buttonSize,
  isBottomPlacement,
  warningText,
  multiple,
  buttonStyle,
  dataTestId,
  noIcon,
  tooltip,
}: ComponentProps) => {
  const selectedValue = Array.isArray(selected) ? selected?.[0]?.value : selected?.value
  const selectedLabel = Array.isArray(selected) ? selected?.[0]?.label : selected?.label
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [position, setPosition] = useState<"top" | "bottom">(isBottomPlacement ? "top" : "bottom")

  const calculatePosition = () => {
    if (!buttonRef.current) return

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - buttonRect.bottom
    const spaceAbove = buttonRect.top
    const dropdownHeight = 320 // Approximate max height of dropdown (max-h-64 = 16rem = 256px) plus padding

    if (isBottomPlacement) {
      setPosition("top")
    } else if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setPosition("top")
    } else {
      setPosition("bottom")
    }
  }

  return (
    <Listbox multiple={multiple} name={name} value={selected} onChange={setSelected} disabled={disabled}>
      {({ open }) => {
        useLayoutEffect(() => {
          if (open) {
            calculatePosition()
            window.addEventListener("resize", calculatePosition)
            return () => window.removeEventListener("resize", calculatePosition)
          }
        }, [open])

        return (
          <>
            {label && (
              <Listbox.Label className={classNames("block text-sm text-gray-700", labelClassName)}>
                {label}
              </Listbox.Label>
            )}
            <div className={classNames("relative", className)}>
              <Listbox.Button
                ref={buttonRef}
                style={{ ...buttonStyle, lineHeight: "1.15" }}
                className={classNames(
                  "bg-white relative w-full border border-gray-300 open-sans rounded-md shadow-sm pl-3 pr-10 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-green-600",
                  disabled && "opacity-50 pointer-events-none",
                  warningText ? "py-1.5" : buttonSize == "large" ? "py-3" : "py-2",
                )}
                data-testid={dataTestId}
              >
                <span className="truncate flex items-center leading-6">
                  {!multiple && !noIcon ? iconForValue(selectedValue, selectedLabel as string) : null}
                  <div className="flex flex-col">
                    <span>
                      {(Array.isArray(selected) ? selected.map((style) => style.label).join(", ") : selectedLabel) ||
                        "\u00A0"}
                    </span>
                    {warningText && <span>{warningText}</span>}
                  </div>
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options
                  className={classNames(
                    className && className,
                    width ? width : "w-full",
                    "absolute z-50 bg-white shadow-lg max-h-64 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none",
                    position === "top" ? "bottom-full mb-1" : "top-full mt-1",
                  )}
                >
                  {options
                    .filter((o) => o.hidden !== true)
                    .map((option) => (
                      <Listbox.Option
                        key={option?.value}
                        disabled={option.disabled}
                        className={({ active, disabled }) =>
                          classNames(
                            active && !disabled && "bg-gray-100",
                            disabled && "cursor-not-allowed opacity-50",
                            "cursor-pointer select-none relative pl-3 pr-9",
                            large ? "py-2.5" : "py-2",
                          )
                        }
                        value={option}
                      >
                        {({ selected, active, disabled }) => (
                          <PortalHoverTip content={option.tooltipText || ""} active={disabled}>
                            <div
                              className="flex items-center"
                              title={(tooltip && (option.label as string)) || undefined}
                            >
                              {!noIcon && iconForValue(option?.value, option.label as string)}
                              <span
                                className={classNames(selected ? "font-medium" : "font-normal", "block truncate mr-2")}
                              >
                                {option.label}
                              </span>

                              {selected ? (
                                <span
                                  className={classNames(
                                    "text-green-600 absolute inset-y-0 right-0 flex items-center pr-4",
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </div>
                          </PortalHoverTip>
                        )}
                      </Listbox.Option>
                    ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )
      }}
    </Listbox>
  )
}

export default SelectInput

const iconForValue = (value: string, label?: string) => {
  if (label == "No electric") return null

  switch (value) {
    case "email":
      return <EnvelopeIcon className="text-gray-400 w-5.5 h-5.5 mr-3.5 flex-shrink-0" />
    case "text":
      return <ChatBubbleLeftIcon className="text-gray-400 w-5.5 h-5.5 mr-3.5 flex-shrink-0" />
    case "":
    case "none":
      return <NoSymbolIcon className="text-gray-400 w-5.5 h-5.5 mr-3.5 flex-shrink-0" />
    case "email_and_text":
      return <MegaphoneIcon className="text-gray-400 w-5.5 h-5.5 mr-3.5 flex-shrink-0" />
    case "single":
      return (
        <Icon.SingleReservation
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    case "recurring":
      return (
        <Icon.RecurringReservation
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    case "group":
      return (
        <Icon.GroupReservation
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    case "separated":
      return (
        <Icon.GroupPaySeparately
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    case "together":
      return (
        <Icon.GroupPayTogether
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    case "rent-only":
      return (
        <Icon.RecurringRent
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    case "rent-and-utilities":
      return (
        <Icon.RecurringRentElectric
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    case "seasonal":
      return (
        <Icon.RecurringSeasonal
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    case "seasonal-and-utilites":
      return (
        <Icon.RecurringSeasonalElectric
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )

    case "blocked":
      return (
        <Icon.BlockedReservation
          className={classNames("w-9 h-9 mr-2 self-center stroke-current text-gray-900 flex-shrink-0")}
        />
      )
    default:
      return null
  }
}
